export interface LoginInput {
  portalCode: string
  email: string
}

export interface VerifyOtpInput {
  verificationId: string
  otp: string
}

export interface ResendOtpInput {
  verificationId: string
}

export interface LoginResponse {
  message: string
  verificationId: string
}

export interface VerifyOtpResponse {
  message: string
  accessToken: string
  refreshToken: string
}

export interface ResendOtpResponse {
  message: string
  verificationId: string
}

export interface UaePassCallbackInput {
  code: string
  state: string
}

export interface UaePassAuthorizeUrlResponse {
  authorizeUrl: string
}

// Mirrors the auth-service UAE_PASS_CALLBACK_OUTCOME enum. The callback no
// longer always logs in — it reports which branch it resolved to and the
// portal acts accordingly (store tokens / show a message / route to sign-up).
export const UAE_PASS_CALLBACK_OUTCOME = {
  LOGIN: 'login',
  EMAIL_EXISTS_USE_OTP: 'email_exists_use_otp',
  SIGNUP_REQUIRED: 'signup_required',
  // Account-Settings enable flow — UAE Pass was linked to the already
  // logged-in user. No tokens; the portal returns the user to Settings.
  LINKED: 'linked',
} as const

export type UaePassCallbackOutcome =
  (typeof UAE_PASS_CALLBACK_OUTCOME)[keyof typeof UAE_PASS_CALLBACK_OUTCOME]

// Verified fields the callback returns for a brand-new UAE PASS user, used to
// pre-fill the sign-up confirm screen. The verified identity backing these
// lives server-side under the signup ticket — the portal never sends it back.
export interface UaePassSignupPrefill {
  firstName: string
  lastName: string
  email: string
  mobile: string
  hasEmiratesId: boolean
}

export interface UaePassCallbackResponse {
  outcome: UaePassCallbackOutcome
  message: string
  email: string
  accessToken: string
  refreshToken: string
  signupTicket: string
  prefill: UaePassSignupPrefill | null
}

export interface UaePassRegisterInput {
  signupTicket: string
  mobileCountryCode: string
  mobile: string
}

export interface UaePassRegisterResponse {
  accessToken: string
  refreshToken: string
  hasEmiratesId: boolean
}

export interface LogoutResponse {
  // Front-channel UAE Pass single-logout URL, present only for UAE Pass
  // sessions — the app redirects the browser here after clearing local state.
  uaePassLogoutUrl: string | null
}

export interface UaePassDisableResponse {
  success: boolean
}

// Mirrors the auth-service LOGIN_METHOD enum — the vocabulary used to compare
// against `LoginMethodSummary.method`.
export const LOGIN_METHOD = {
  EMAIL_OTP: 'email_otp',
  MOBILE_OTP: 'mobile_otp',
  PASSKEY: 'passkey',
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  UAE_PASS: 'uae_pass',
} as const

export interface LoginMethodSummary {
  // Deliberately `string`, not a union of the values above: the enabled-method
  // count drives the "can't disable your only login method" guard, so a method
  // the portal doesn't recognize yet must still be counted rather than dropped.
  method: string
  verifiedAt: string | null
}

// Only enabled methods are returned — an absent method means "not enabled".
export interface LoginMethodsResponse {
  methods: LoginMethodSummary[]
}

export interface PrivacyPolicyResponse {
  message: string
  content: string
}

export interface TermsAndConditionsResponse {
  message: string
  content: string
}
